/** Small interactive chip/row/rail components for the UGC Studio builder. */
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useUgcLight } from "./ugc-theme";

interface RailButtonProps {
  label: string;
  icon?: ReactNode;
  active: boolean;
  onClick: () => void;
}

export function RailButton({ label, icon, active, onClick }: RailButtonProps) {
  const L = useUgcLight();
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium rounded-xl border transition-all ${
        active
          ? L
            ? "bg-[#ef3e29]/10 border-[#ef3e29]/50 text-gray-900"
            : "bg-[#ef3e29]/15 border-[#ef3e29]/40 text-white shadow-[0_0_0_1px_rgba(239,62,41,0.15),inset_0_1px_0_rgba(255,255,255,0.04)]"
          : L
            ? "bg-transparent border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50"
            : "bg-white/[0.02] border-white/[0.06] text-white/55 hover:text-white/85 hover:border-white/15 hover:bg-white/[0.04]"
      }`}
    >
      {icon ? (
        <span className={active ? "text-[#ef3e29]" : L ? "text-gray-400" : "text-white/50"}>
          {icon}
        </span>
      ) : null}
      <span>{label}</span>
    </button>
  );
}

interface ChipRowProps {
  label: string;
  value: string;
  onClick: () => void;
  accent?: boolean;
}

export function ChipRow({ label, value, onClick, accent }: ChipRowProps) {
  const L = useUgcLight();
  return (
    <div className="flex items-center gap-3">
      <span
        className={`text-[10px] uppercase tracking-[0.15em] font-semibold w-20 flex-shrink-0 ${L ? "text-gray-400" : "text-white/35"}`}
      >
        {label}
      </span>
      <button
        onClick={onClick}
        className={`group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
          accent
            ? L
              ? "bg-[#ef3e29]/10 border-[#ef3e29]/40 text-gray-900 hover:bg-[#ef3e29]/15 hover:border-[#ef3e29]/60"
              : "bg-[#ef3e29]/10 border-[#ef3e29]/30 text-white hover:bg-[#ef3e29]/15 hover:border-[#ef3e29]/50"
            : L
              ? "bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200 hover:border-gray-300"
              : "bg-white/[0.04] border-white/[0.08] text-white/80 hover:bg-white/[0.08] hover:border-white/20"
        }`}
      >
        {value}
        <ChevronRight className="w-3 h-3 opacity-50 group-hover:opacity-100 transition" />
      </button>
    </div>
  );
}

interface ChipGroupProps<T extends string | number> {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  renderLabel?: (v: T) => string;
}

export function ChipGroup<T extends string | number>({
  label,
  options,
  value,
  onChange,
  renderLabel,
}: ChipGroupProps<T>) {
  const L = useUgcLight();
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span
        className={`text-[10px] uppercase tracking-[0.15em] font-semibold w-20 flex-shrink-0 ${L ? "text-gray-400" : "text-white/35"}`}
      >
        {label}
      </span>
      <div className="flex gap-1.5 flex-wrap">
        {options.map((opt) => {
          const active = opt === value;
          return (
            <button
              key={String(opt)}
              onClick={() => onChange(opt)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                active
                  ? "bg-[#ef3e29] text-white border-[#ef3e29] shadow-[0_0_0_3px_rgba(239,62,41,0.18)]"
                  : L
                    ? "bg-transparent border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-800 hover:border-gray-300"
                    : "bg-white/[0.03] border-white/[0.08] text-white/55 hover:bg-white/[0.06] hover:text-white/85 hover:border-white/15"
              }`}
            >
              {renderLabel ? renderLabel(opt) : String(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
